import type { CropData, BunnyUploadResult, BunnyCredentialsResponse, ProductCategory } from '@/types';
import { logger } from '@/lib/logger';
import { generateSlug } from '@/constants/patterns';
import { fetchWithRetry } from '@/lib/fetchWithRetry';

export class BunnyUploadService {
  async uploadCrops(
    crops: CropData[],
    credentials: BunnyCredentialsResponse,
    productTitle: string,
    fetchReadmePdf?: (category: ProductCategory) => Promise<Blob | null>,
    productCategory?: ProductCategory
  ): Promise<BunnyUploadResult> {
    const slug = generateSlug(productTitle);
    const { storageZone, storagePassword, pullZone } = credentials;
    const storageApi = `https://storage.bunnycdn.com/${storageZone}`;

    const printFileUrls = await this.uploadCropFiles(crops, slug, storageApi, storagePassword, pullZone);

    if (productCategory && fetchReadmePdf) {
      await this.uploadReadmePdf(slug, productCategory, storageApi, storagePassword, fetchReadmePdf);
    }

    return { slug, printFileUrls };
  }

  async uploadFile(
    blob: Blob,
    fileName: string,
    contentType: string,
    credentials: BunnyCredentialsResponse
  ): Promise<string> {
    const { storageZone, storagePassword, pullZone } = credentials;
    const storageApi = `https://storage.bunnycdn.com/${storageZone}`;
    const uploadUrl = `${storageApi}/${fileName}`;

    const arrayBuffer = await blob.arrayBuffer();
    const response = await fetchWithRetry(uploadUrl, {
      method: 'PUT',
      headers: {
        'AccessKey': storagePassword,
        'Content-Type': contentType,
      },
      body: arrayBuffer,
      timeout: 60000,
      maxRetries: 3,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload ${fileName} to BunnyCDN`);
    }

    return `https://${pullZone}/${fileName}`;
  }

  private async uploadCropFiles(
    crops: CropData[],
    slug: string,
    storageApi: string,
    storagePassword: string,
    pullZone: string
  ): Promise<Record<string, string>> {
    const printFileUrls: Record<string, string> = {};

    for (const crop of crops) {
      if (!crop.blob) {
        throw new Error(`Missing blob for ${crop.ratio}`);
      }

      const fileName = `${slug}/${crop.ratio}/${crop.ratio}.jpg`;
      const uploadUrl = `${storageApi}/${fileName}`;
      const arrayBuffer = await crop.blob.arrayBuffer();

      const response = await fetchWithRetry(uploadUrl, {
        method: 'PUT',
        headers: {
          'AccessKey': storagePassword,
          'Content-Type': 'image/jpeg',
        },
        body: arrayBuffer,
        timeout: 60000,
        maxRetries: 3,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${fileName} to BunnyCDN`);
      }

      printFileUrls[crop.ratio] = `https://${pullZone}/${fileName}`;
    }

    return printFileUrls;
  }

  private async uploadReadmePdf(
    slug: string,
    productCategory: ProductCategory,
    storageApi: string,
    storagePassword: string,
    fetchReadmePdf: (category: ProductCategory) => Promise<Blob | null>
  ): Promise<void> {
    logger.debug('BunnyUpload', `Uploading README PDF for category: ${productCategory}`);

    try {
      const pdfBlob = await fetchReadmePdf(productCategory);
      if (!pdfBlob) return;

      const arrayBuffer = await pdfBlob.arrayBuffer();
      const fileName = `${slug}/README.pdf`;
      const uploadUrl = `${storageApi}/${fileName}`;

      const response = await fetchWithRetry(uploadUrl, {
        method: 'PUT',
        headers: {
          'AccessKey': storagePassword,
          'Content-Type': 'application/pdf',
        },
        body: arrayBuffer,
        timeout: 60000,
        maxRetries: 3,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('BunnyUpload', 'README upload failed', response.status, errorText);
        throw new Error(`Failed to upload README.pdf: ${response.status}`);
      }

      logger.debug('BunnyUpload', 'README PDF uploaded successfully');
    } catch (error) {
      logger.error('BunnyUpload', 'Failed to upload README PDF', error);
    }
  }
}
